import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Toaster } from "sonner";
import {
  ArrowDown,
  Facebook,
  Twitter,
  Instagram,
  Mail,
  Flame,
} from "lucide-react";
import useLenis from "@/lib/useLenis";
import { Reveal, MaskLine } from "@/components/Reveal";
import DonationForm from "@/components/DonationForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = {
  inside:
    "https://customer-assets-jt897jd0.emergentagent.net/job_e6b31f43-72b1-4697-a6e1-e5574a492f4f/artifacts/ft9t1wp9_ChatGPT%20Image%20Aug%2012%2C%202026%2C%2008_15_59%20PM.png",
  road: "https://customer-assets-jt897jd0.emergentagent.net/job_e6b31f43-72b1-4697-a6e1-e5574a492f4f/artifacts/hxhlb6yw_exec-4b7fb985-a182-4b67-ba10-08e9feb7edc4.png",
  logo: "https://customer-assets-jt897jd0.emergentagent.net/job_e6b31f43-72b1-4697-a6e1-e5574a492f4f/artifacts/lzhau4w1_walterstown%20logo.jpg",
};

const nf = (n) => new Intl.NumberFormat("en-IE").format(Math.round(n));

/* ---------- Segmented progress bar (three-colour funding breakdown) ---------- */
const FUND_COLORS = {
  secured: "#FFFFFF", // Meath Partnership funding
  raised: "#43C46A", // Fundraising so far
  remaining: "#E5484D", // Still to raise
};

const SegmentedBar = ({ securedPct = 0, fundraisingPct = 0 }) => {
  const segments = 40;
  const securedFilled = Math.round((securedPct / 100) * segments);
  const fundFilled = Math.round((fundraisingPct / 100) * segments);
  const activeTo = securedFilled + fundFilled;
  const tickColor = (i) => {
    if (i < securedFilled) return FUND_COLORS.secured;
    if (i < activeTo) return FUND_COLORS.raised;
    return null;
  };
  return (
    <div className="flex h-8 w-full gap-[3px]" data-testid="progress-bar">
      {Array.from({ length: segments }).map((_, i) => {
        const c = tickColor(i);
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0.15, scaleY: 0.4 }}
            whileInView={{ opacity: 1, scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.015 }}
            className="flex-1"
            style={{ backgroundColor: c || FUND_COLORS.remaining }}
          />
        );
      })}
    </div>
  );
};

const LegendItem = ({ color, label, value, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="mt-[2px] h-3.5 w-3.5 flex-shrink-0" style={{ backgroundColor: color }} />
    <div>
      <p className="label-caps text-white/55">{label}</p>
      <p className="font-oswald text-2xl font-bold leading-tight text-white">€{nf(value)}</p>
    </div>
  </div>
);

/* ---------- Hero ---------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative flex h-screen min-h-[680px] flex-col justify-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMG.inside} alt="The new Ball Wall, viewed from inside the astro pitches" className="h-full w-full object-cover grayscale" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/20" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-screen-2xl px-4 pb-16 md:px-8 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-[1px] w-12 bg-white/60" />
          <span className="label-caps">Walterstown GFC · Club Development Campaign</span>
        </motion.div>

        <h1 className="font-oswald text-[16vw] font-bold uppercase leading-[0.82] tracking-tighter text-white md:text-[11vw]">
          <MaskLine delay={0.15}>Build</MaskLine>
          <MaskLine delay={0.3}>Our Future</MaskLine>
        </h1>

        <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <MaskLine delay={0.5} className="font-oswald text-2xl uppercase tracking-wide text-white/80 md:text-4xl">
            The New Ball Wall &amp; Astro Pitch Redevelopment
          </MaskLine>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="max-w-md text-base text-white/80 md:text-right"
          >
            A permanent legacy for our club. Donate €1,000 and put your name — or your
            family, business or a memory — on the wall forever.
          </motion.p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
        className="absolute bottom-6 right-4 z-10 flex items-center gap-2 md:right-8"
      >
        <span className="label-caps">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-white/80" />
      </motion.div>
    </section>
  );
};

/* ---------- Stats / Command Center ---------- */
const CommandCenter = ({ stats }) => {
  if (!stats) return null;
  const metrics = [
    { label: "Total Project Cost", value: `€${nf(stats.total_project_cost_eur)}` },
    { label: "Funding Secured", value: `€${nf(stats.funding_secured_eur)}`, note: "Meath Partnership are providing funding towards the resurfacing of the Astro Pitches." },
    { label: "Balance Required", value: `€${nf(stats.loan_approved_eur)}`, sub: "Fundraising Target" },
  ];
  return (
    <section id="progress" className="border-t border-white/15 py-24 md:py-32">
      <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-8">
        <Reveal className="mb-16 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="label-caps mb-4">02 / The Numbers</p>
            <h2 className="font-oswald text-5xl font-bold uppercase leading-none tracking-tighter md:text-6xl">
              The Community Goal
            </h2>
          </div>
          <p className="max-w-sm text-white/70">
            €170,000 project. €100,000 secured. Help us raise the final €70,000.
          </p>
        </Reveal>

        {/* Project figures */}
        <div className="mb-8 grid grid-cols-1 gap-px border border-white/15 bg-white/15 md:grid-cols-3">
          {metrics.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.1} className="bg-black">
              <div className="p-8 md:p-10">
                <p className="label-caps mb-6">{m.label}</p>
                <p className="font-oswald text-4xl font-bold leading-none text-white md:text-5xl">
                  {m.value}
                </p>
                {m.sub && <p className="mt-3 text-sm text-white/60">{m.sub}</p>}
                {m.note && <p className="mt-2 text-sm leading-relaxed text-white/60">{m.note}</p>}
              </div>
            </Reveal>
          ))}
        </div>

        {/* Running total */}
        <Reveal className="border border-white/20 bg-[#050505] p-8 md:p-12">
          <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="label-caps mb-3">Raised So Far</p>
              <p className="font-oswald text-6xl font-bold leading-none text-white md:text-8xl" data-testid="raised-amount">
                €{nf(stats.raised_eur)}
              </p>
              <p className="mt-3 text-sm text-white/65">
                Includes €{nf(stats.secured_eur)} funding already secured from Meath Partnership to part fund the Astro Resurfacing
              </p>
            </div>
            <div className="md:text-right">
              <p className="label-caps mb-3">Total Project Cost</p>
              <p className="font-oswald text-4xl font-medium leading-none text-white/50 md:text-5xl">
                €{nf(stats.goal_eur)}
              </p>
            </div>
          </div>
          <SegmentedBar securedPct={stats.secured_pct} fundraisingPct={stats.fundraising_pct} />
          <div className="mt-3">
            <span className="label-caps" data-testid="percent-label">{stats.percent}% Funded</span>
          </div>
          <div className="mt-8 flex flex-col gap-5 border-t border-white/12 pt-8 sm:flex-row sm:flex-wrap sm:gap-x-12">
            <LegendItem
              testid="legend-secured"
              color={FUND_COLORS.secured}
              label="Meath Partnership Funding"
              value={stats.secured_eur}
            />
            <LegendItem
              testid="legend-fundraising"
              color={FUND_COLORS.raised}
              label="Fundraising So Far"
              value={stats.fundraising_eur}
            />
            <LegendItem
              testid="legend-remaining"
              color={FUND_COLORS.remaining}
              label="Still To Raise"
              value={stats.still_to_raise_eur}
            />
          </div>

          {stats.recent_donor_count >= stats.recent_min && (
            <div
              className="mt-8 flex items-center gap-3 border border-[#43C46A]/40 bg-[#43C46A]/10 px-5 py-4"
              data-testid="recent-donors-note"
            >
              <Flame className="h-5 w-5 flex-shrink-0 text-[#43C46A]" />
              <p className="text-sm text-white/85 md:text-base">
                <span className="font-oswald font-bold text-white">{stats.recent_donor_count} supporters</span>{" "}
                have generously donated €1,000 in the past week.
              </p>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- Manifesto / Vision ---------- */
const Manifesto = () => (
  <section id="vision" className="border-t border-white/15 py-24 md:py-32">
    <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-8">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
        <Reveal className="lg:col-span-5">
          <p className="label-caps mb-4">01 / The Vision</p>
          <h2 className="font-oswald text-5xl font-bold uppercase leading-[0.9] tracking-tighter md:text-6xl">
            A New Club Development to Last Generations
          </h2>
          <p className="mt-8 text-lg leading-relaxed text-white/80">
            We are delighted to announce the redevelopment of our Astro Pitch area. We are
            replacing the road side boundary of the current Astro Pitches with a full length, 5m
            high state of the art Ball Wall. The current fixed internal dividers will be replaced
            with retractable nets, allowing for the full area to be used as a training area when
            required. We are also replacing the surface with a brand new premium standard all
            weather playing surface.
          </p>
          <p className="mt-6 text-lg leading-relaxed text-white/80">
            The new Ball Wall &amp; astro resurfacing gives every member — from the youngest
            nursery player to our senior teams — a premium quality facility to develop &amp; enhance
            their football skills,
            all year round. It is a huge step forward in the ongoing development of our great club.
          </p>
          <p className="mt-6 text-lg leading-relaxed text-white/80">
            In addition to the redevelopment of the Astros, we are installing new goals on the
            juvenile pitch and training area, along with new ball stops on the road side of the
            juvenile pitch, and between the training area and back pitch. This, along with the new
            LED lights which were installed last year, will greatly enhance the training facilities
            for the coming Autumn / Winter.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="relative border border-white/20">
            <img src={IMG.inside} alt="Ball Wall viewed from inside the astro pitches" className="aspect-[16/10] w-full object-cover" />
            <span className="absolute bottom-0 left-0 bg-white px-4 py-2 font-oswald text-xs uppercase tracking-[0.2em] text-black">
              The Vision — Inside the Astro Pitches
            </span>
          </div>
          <div className="relative mt-6 border border-white/20">
            <img src={IMG.road} alt="Ball Wall road-side view with club crests" className="aspect-[16/8] w-full object-cover" />
            <span className="absolute bottom-0 left-0 bg-white px-4 py-2 font-oswald text-xs uppercase tracking-[0.2em] text-black">
              Road-Side View
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- Donation Section ---------- */
const Donate = () => (
  <section id="donate" className="border-t border-white/15 py-24 md:py-32">
    <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-8">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="label-caps mb-4">03 / Get Your Name On The Wall</p>
            <h2 className="font-oswald text-5xl font-bold uppercase leading-[0.9] tracking-tighter md:text-6xl">
              Leave your legacy
            </h2>
            <p className="mt-8 text-lg text-white/80">
              Have your family name, business, or a treasured memory engraved on a lasting plaque
              on our new Ball Wall — a permanent thank-you to the supporters who built it.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <DonationForm />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Footer ---------- */
const Footer = () => (
  <footer className="border-t border-white/15 pt-24">
    <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-8">
      <div className="mb-16 flex flex-col items-center gap-6 border-b border-white/15 pb-16 text-center">
        <img src={IMG.logo} alt="Walterstown GFC crest" className="h-24 w-24 object-contain" />
        <p className="max-w-md text-white/70">
          Build Our Future — help us complete the new Ball Wall &amp; astro resurfacing for every
          member of Walterstown GFC.
        </p>
      </div>
      <div className="mb-16 flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <a
          href="mailto:treasurer.walterstown.meath@gaa.ie"
          data-testid="footer-email-link"
          className="group flex items-center gap-3 font-oswald text-lg uppercase tracking-wide text-white/70 transition-colors hover:text-white"
        >
          <Mail className="h-5 w-5" />
          treasurer.walterstown.meath@gaa.ie
        </a>
        <div className="flex items-center gap-6">
          {[
            { icon: Facebook, href: "https://facebook.com/gfcwalterstown", id: "facebook" },
            { icon: Twitter, href: "https://twitter.com/gfcwalterstown", id: "twitter" },
            { icon: Instagram, href: "https://instagram.com/walterstowngfc", id: "instagram" },
          ].map((s) => (
            <a
              key={s.id}
              data-testid={`social-${s.id}-link`}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 transition-colors hover:text-white"
            >
              <s.icon className="h-6 w-6" />
            </a>
          ))}
        </div>
      </div>
    </div>
    <div className="overflow-hidden border-t border-white/15">
      <h2 className="whitespace-nowrap px-4 py-8 text-center font-oswald text-[18vw] font-bold uppercase leading-none tracking-tighter text-white md:text-[13vw]">
        Walterstown
      </h2>
    </div>
    <div className="border-t border-white/15 py-6">
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col items-center justify-between gap-2 px-4 text-center md:flex-row md:px-8 md:text-left">
        <p className="label-caps">© {new Date().getFullYear()} Walterstown GFC</p>
        <p className="label-caps">Build Our Future · Ball Wall Campaign</p>
      </div>
    </div>
  </footer>
);

/* ---------- Page ---------- */
export default function BallWall() {
  useLenis();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/ballwall/stats`)
      .then((r) => setStats(r.data))
      .catch((e) => console.error("stats error", e));
  }, []);

  useEffect(() => {
    try {
      let vid = localStorage.getItem("bw_visitor_id");
      if (!vid) {
        vid =
          (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
          `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("bw_visitor_id", vid);
      }
      const params = new URLSearchParams(window.location.search);
      const source = params.get("from") || params.get("utm_source") || "direct";
      axios
        .post(`${API}/track/visit`, {
          visitor_id: vid,
          source,
          path: window.location.pathname,
          referrer: document.referrer || "",
        })
        .catch(() => {});
    } catch (_) {
      /* tracking is best-effort */
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-black">
      <div className="noise-overlay" />
      <Toaster theme="dark" position="top-center" />

      {/* Nav */}
      <nav className="fixed top-0 z-50 w-full border-b border-white/10 bg-black/70 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-screen-2xl items-center justify-between px-4 py-4 md:px-8">
          <span className="flex items-center gap-3 font-oswald text-lg font-bold uppercase tracking-widest text-white">
            <img src={IMG.logo} alt="Walterstown GFC crest" className="h-9 w-9 object-contain" />
            Walterstown GFC
          </span>
          <a
            href="#donate"
            data-testid="nav-donate-btn"
            className="rounded-none bg-white px-5 py-2 font-oswald text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-white/80"
          >
            Donate €1,000
          </a>
        </div>
      </nav>

      <Hero />

      {/* Marquee */}
      <div className="border-y border-white/15 bg-black py-5">
        <Marquee speed={40} gradient={false}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="mx-8 font-oswald text-2xl uppercase tracking-widest text-white/80">
              Leave Your Legacy <span className="mx-8 text-white/30">•</span> Build Our Future
              <span className="mx-8 text-white/30">•</span> Get Your Name On The Wall
              <span className="mx-8 text-white/30">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      <Manifesto />
      <CommandCenter stats={stats} />
      <Donate />
      <Footer />
    </div>
  );
}
